// Тексты «как встанет персонаж» для юзера. Сами режимы считает РЕЗОЛВЕР
// бэка (И4): character_fit приходит в /providers (витрина возможностей),
// mode выбранного персонажа — из /plan. Здесь только словарь формулировок —
// никаких правил раскладки на фронте.

export interface CharacterFit {
	mode: string
	count?: number
	warnings?: string[]
}

/** Строка в справку модели («?») — ДО выбора персонажа. */
export function characterFitLine(fit?: CharacterFit | null): { text: string; warn: boolean } | null {
	if (!fit?.mode) return null
	switch (fit.mode) {
		case 'FACE_SWAP_EXCLUDED':
			return { text: 'в замене лица не используется — переносится только маска лица, внешность потеряется', warn: true }
		case 'NO_IMAGE_INPUT':
			return { text: 'не применяется — модель не принимает фото', warn: false }
		case 'first_frame_only':
			return { text: 'встанет только в первый кадр — внешность возьмётся с одного портрета', warn: true }
		case 'positional':
			return { text: 'займёт слот лица — порядок картинок у этой модели важен', warn: false }
		default: {
			const n = fit.count || 0
			return n > 1
				? { text: `встанет набором до ${n} фото внешности`, warn: false }
				: { text: 'встанет одним фото внешности (портрет или лист-карточка)', warn: false }
		}
	}
}

/** Почему персонаж не подошёл — тексты кодов unfit (пикер показывает строкой). */
export const UNFIT_TEXTS: Record<string, string> = {
	// Персонаж на этапе создания: эталон ещё не собран. Раньше бэк молча
	// подставлял сырой портрет из слота — юзер думал, что работает персонаж,
	// а сходство было как от «просто приложил фото» (жалоба владельца).
	NOT_BUILT: 'Аватар ещё не собран — закончите создание в кабинете',
	NO_PORTRAIT: 'У аватара нет портрета — добавьте его в кабинете',
	UNKNOWN_MODEL: 'Эта модель не умеет принимать аватара',
	NO_IMAGE_INPUT: 'Эта модель не принимает фото — аватару некуда встать',
	FACE_SWAP_EXCLUDED: 'В замене лица аватар не используется — загрузите фото вручную',
}

/** Почему сцена не встала фото (коды unfit резолвера наборов) — персонажи, локации и предметы тогда идут в
 *  промпт словами: именем и описанием. */
export const SCENE_UNFIT_TEXTS: Record<string, string> = {
	NO_IMAGE_INPUT: 'Эта нейросеть не принимает фото — аватары, локации и предметы уйдут в промпт словами',
	UNKNOWN_MODEL: 'Эта нейросеть не берёт фото аватаров, локаций и предметов — они уйдут в промпт словами',
	FACE_SWAP_EXCLUDED: 'Замена лица берёт только два загруженных фото — аватары, локации и предметы здесь не используются',
	NO_VIEWS: 'У выбранного ещё нет готовых фото',
}
export const SCENE_UNFIT_DEFAULT = SCENE_UNFIT_TEXTS.UNKNOWN_MODEL

export interface SceneNoticeLine {
	code: string
	message: string
	blocking?: boolean
}

// Оговорки резолвера про сами фото в слотах: без положенных фото они неправда.
const PLACEMENT_ONLY = new Set(['W_FIRST_FRAME', 'W_TRIMMED_TO_ONE', 'W_NEED_SCENE', 'W_WORDS_ONLY'])

/** Строки статуса сцены над промптом — по тому, что РЕАЛЬНО легло в слоты. Резолвер считает раскладку по модели,
 *  но сколько фото поместилось, знает виджет (слоты могут быть заняты фото юзера): «словами» называем сами —
 *  по итогу, а не по плану. Пока фото ассетов в слотах нет, барьер «сцена станет первым кадром» не действует. */
export function sceneNoticeLines(p: {
	fits: boolean
	reason?: string | null
	warnings: SceneNoticeLine[]
	/** Имена готовых ассетов, чьих фото в слотах нет (неготовые названы оговоркой «Ещё не готовы»). */
	wordsOnly: string[]
	/** Положено ли хоть одно фото. */
	placed: boolean
	/** Единственный слот занят фото юзера — фото ассета его не вытесняло. */
	slotKept?: boolean
}): SceneNoticeLine[] {
	const out = p.warnings.filter((w) => (p.placed ? w.code !== 'W_WORDS_ONLY' : !PLACEMENT_ONLY.has(w.code)))
	if (!p.fits) {
		// NO_VIEWS уже сказано оговоркой «Ещё не готовы: …».
		if (p.reason !== 'NO_VIEWS') out.push({ code: 'W_NO_PHOTOS', message: SCENE_UNFIT_TEXTS[p.reason ?? ''] ?? SCENE_UNFIT_DEFAULT })
		return out
	}
	if (p.wordsOnly.length) {
		const names = p.wordsOnly.join(', ')
		const go = p.wordsOnly.length > 1 ? 'уйдут' : 'уйдёт'
		// Совет «уберите своё фото» — только там, где фото из сцены после этого действительно сработает: у нейросети,
		// которая лишь оживляет кадр, оно стало бы первым кадром и запуск был бы закрыт.
		const frameOnly = p.warnings.some((w) => w.blocking)
		out.push({
			code: 'W_WORDS_ONLY',
			message: p.slotKept
				? `В слоте ваше фото — ${names} ${go} в промпт словами.${frameOnly ? '' : ' Чтобы нейросеть получила фото из сцены, уберите своё из слота.'}`
				: `Не поместились в нейросеть: ${names} — ${go} в промпт словами.`,
		})
	}
	return out
}

/** Уложить раскладку резолвера в оставшееся место, если его стало меньше, чем при запросе плана (юзер успел
 *  догрузить фото, слоты вернул снимок): сперва главный вид каждого ассета, потом вторые виды — то же правило,
 *  что у резолвера. Простая обрезка с конца оставила бы первому ассету два вида, а последнему ни одного. */
export function fitLayout<T extends { asset_id: string }>(items: T[], room: number): T[] {
	if (room <= 0) return []
	if (items.length <= room) return items
	const depth = new Map<T, number>()
	const seen: Record<string, number> = {}
	for (const i of items) {
		depth.set(i, seen[i.asset_id] ?? 0)
		seen[i.asset_id] = (seen[i.asset_id] ?? 0) + 1
	}
	const keep = new Set([...items].sort((a, b) => (depth.get(a) ?? 0) - (depth.get(b) ?? 0)).slice(0, room))
	return items.filter((i) => keep.has(i))
}

/** Сколько ещё фото ассетов примет канал виджета: ёмкость минус фото самого юзера. */
export function sceneRoom(p: {
	channel: AssetChannel
	/** Сколько фото юзера (не сцены) уже в этом канале. */
	userPhotos: number
	/** maxImages модели. */
	maxImages?: number | null
	/** Предел фото карточки (user_photo_limit). */
	photoLimit?: number | null
	/** Предел отдельного канала референсов (omni): 7 или 4 при видео-референсе. */
	omniLimit: number
}): number {
	if (p.channel === 'uploaded') return p.userPhotos > 0 ? 0 : 1
	if (p.channel === 'omni') return Math.max(0, p.omniLimit - p.userPhotos)
	const max = Math.max(1, p.maxImages ?? 1)
	const cap = p.photoLimit ? Math.max(1, Math.min(max, p.photoLimit)) : max
	return Math.max(0, cap - p.userPhotos)
}

export type AssetChannel = 'uploaded' | 'references' | 'omni'

/** В какой канал виджета класть фото ассетов сцены (персонажей, локаций, предметов).
 *
 * Резолвер (бэк) решает СКОЛЬКО фото и семантику; КУДА их класть — знает
 * фронт по флагам модели. Канал обязан совпасть с тем слотом, что виджет
 * РЕНДЕРИТ и шлёт бэку, иначе фото уходит в невидимый/чужой канал:
 *  - omni-модели (Gemini Omni, supportsReferenceImages) читают отдельный
 *    reference_image_urls — не общий image_input;
 *  - одиночные модели (seedance: единственный слот кадра, maxImages=1) вовсе
 *    не рендерят мульти-ряд referenceImages — фото туда «пропадало».
 * Приоритет omni над мульти: у omni-видео свой identity-канал.
 */
export function pickAssetChannel(p: {
	supportsReferenceImages?: boolean
	supportsMultipleImages: boolean
	/** Seedance-2 в режиме «Кадры»: единственный видимый слот — первый кадр
	 *  (uploadedImage). Мульти-ряд референсов (reference_image_urls) виджет
	 *  показывает только в режиме «Референсы». Без этой ветки эталон уходил в
	 *  скрытый мульти-ряд (у seedance maxImages>1) — «ничего не подставилось». */
	isSeedanceFrames?: boolean
}): AssetChannel {
	if (p.supportsReferenceImages) return 'omni'
	if (p.isSeedanceFrames) return 'uploaded'
	if (p.supportsMultipleImages) return 'references'
	return 'uploaded'
}

/** Показывать ли кнопку-вход «Персонаж» рядом со слотами.
 *
 * Правило одно на оба ряда (десктопный UploadsRow и мобильный бар
 * GenerateInputBar) — раньше жило инлайном в UploadsRow, из-за чего на мобиле
 * входа не было вовсе. Персонажи открыты всем авторизованным (staff-гейт
 * мягкого запуска снят 2026-07-31): гостю вход не показываем — ни выбрать,
 * ни сохранить персонажа он всё равно не может.
 */
export function characterEntryVisible(p: {
	isAuthed: boolean
	hasSelection: boolean
	fitMode?: string
	isAudio?: boolean
	isPresentation?: boolean
}): boolean {
	return (
		p.isAuthed &&
		!p.hasSelection &&
		!!p.fitMode &&
		p.fitMode !== 'NO_IMAGE_INPUT' &&
		p.fitMode !== 'FACE_SWAP_EXCLUDED' &&
		!p.isAudio &&
		!p.isPresentation
	)
}
